import type { ReactNode } from "react";

// 한국어 조사가 URL 바로 뒤에 붙어도("https://a.com에서") 링크가 깨지지 않도록 ASCII 범위만 매칭하되,
// "/", "=", "#" 바로 뒤에 오는 한글 경로·검색어("wiki/대한민국", "?q=수학")는 URL에 포함
const URL_REGEX = /(?:https?:\/\/|www\.)(?:[!-~]*[/=#][^\s!-~][^\s]*|[!-~]+)/gi;
const TRAILING_PUNCTUATION = /[.,!?;:'"`*]+$/;
const BRACKET_PAIRS: Record<string, string> = { ")": "(", "]": "[", "}": "{", ">": "<" };

const countChar = (text: string, char: string) => text.split(char).length - 1;

// 문장 끝 마침표, 괄호로 감싼 링크 "(https://a.com)" 등 URL에 속하지 않는 꼬리 문자를 제거
function trimUrl(raw: string) {
  let url = raw;
  for (;;) {
    const trimmed = url.replace(TRAILING_PUNCTUATION, "");
    const last = trimmed.slice(-1);
    const open = BRACKET_PAIRS[last];
    if (open && countChar(trimmed, last) > countChar(trimmed, open)) {
      url = trimmed.slice(0, -1);
      continue;
    }
    if (trimmed === url) return url;
    url = trimmed;
  }
}

function toHref(url: string) {
  const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  try {
    const parsed = new URL(href);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    if (href !== url && !/\.[a-z0-9-]+$/i.test(parsed.hostname)) return null;
    return parsed.href;
  } catch {
    return null;
  }
}

// 긴 주소는 말풍선을 넘치지 않도록 도메인 + 경로 일부만 표시 (전체 주소는 툴팁)
function toLabel(url: string) {
  const withoutProtocol = url.replace(/^https?:\/\//i, "");
  return withoutProtocol.length > 48 ? `${withoutProtocol.slice(0, 45)}…` : withoutProtocol;
}

export function renderTextWithLinks(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const regex = new RegExp(URL_REGEX.source, URL_REGEX.flags);
  let lastIndex = 0;
  let matchIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const url = trimUrl(match[0]);
    const href = toHref(url);
    if (!href) continue;
    const start = match.index;
    if (start > lastIndex) {
      nodes.push(text.slice(lastIndex, start));
    }
    nodes.push(
      <a
        key={`${keyPrefix}-link-${matchIndex++}`}
        className="chat-link"
        href={href}
        title={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {toLabel(url)}
      </a>
    );
    lastIndex = start + url.length;
    regex.lastIndex = lastIndex;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes.length === 0 ? [text] : nodes;
}
